// src/components/kennels/KennelGrid.tsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Popover,
  Paper,
  Tooltip,
} from '@mui/material';
import { TodayButton, StyledIconButton } from '../common/Buttons';
import {
  ChevronLeft,
  ChevronRight,
  DateRange,
  Close,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import DateRangePicker from '../../components/forms/shared/DateRangePicker';
import {
  kennelsAPI,
  bookingsAPI,
  KennelGrouped,
  BookingGridItem,
} from '../../services/api';
import { colors } from '../../theme/theme';
import {
  DATE_COL_WIDTH,
  KENNEL_COL_WIDTH,
  GridWrapper,
  ControlsBar,
  DatePickerWrapper,
  GridScroller,
  StickyHeaderRow,
  StickyDateCorner,
  KennelHeaderCell,
  DateRow,
  StickyDateCell,
  DateLabel,
  EmptyKennelCell,
  BookingCell,
  CellLabel,
  CellMidChar,
  StyledTabs,
  StyledTab,
} from './KennelGridElements';
import { usePermissions } from '../../hooks/usePermissions';

const GRID_DAYS = 32;

type CellRole = 'top' | 'mid' | 'bot' | 'solo';

interface CellInfo {
  role: CellRole;
  booking: BookingGridItem;
  surnameCharIndex: number;
}

interface PopoverState {
  anchorEl: HTMLElement;
  booking: BookingGridItem;
}

interface GridTab {
  label: string;
  kennels: KennelGrouped['kennels'];
}

const TAB_ORDER = ['A Triple', 'A Double', 'B Double', 'B Single', 'Cattery'];

const deriveTabs = (data: KennelGrouped[]): GridTab[] => {
  const tabs: GridTab[] = [];

  for (const group of data) {
    const sideName = group.kennelSide.name;
    const isCattery = sideName.toLowerCase().includes('cattery');

    if (isCattery) {
      tabs.push({ label: 'Cattery', kennels: group.kennels });
      continue;
    }

    const letter = sideName.replace(/side/i, '').trim().toUpperCase();

    const bySize = new Map<string, KennelGrouped['kennels']>();
    for (const k of group.kennels) {
      const size = k.size ?? 'unknown';
      if (!bySize.has(size)) bySize.set(size, []);
      bySize.get(size)!.push(k);
    }

    for (const [size, kennels] of bySize) {
      const label = `${letter} ${size.charAt(0).toUpperCase() + size.slice(1)}`;
      tabs.push({ label, kennels });
    }
  }

  tabs.sort((a, b) => {
    const ia = TAB_ORDER.indexOf(a.label);
    const ib = TAB_ORDER.indexOf(b.label);
    if (ia !== -1 && ib !== -1) return ia - ib;
    if (ia !== -1) return -1;
    if (ib !== -1) return 1;
    return a.label.localeCompare(b.label);
  });

  return tabs;
};

const getBookingColor = (status: string, highlighted = false): string => {
  if (highlighted) return '#2e7d32';
  switch (status.toLowerCase()) {
    case 'confirmed':
      return '#1565c0';
    case 'pending':
      return '#c62828';
    default:
      return '#388e3c';
  }
};

const buildDatesBetween = (start: string, end: string): string[] => {
  const dates: string[] = [];
  let current = dayjs(start);
  const last = dayjs(end);
  while (!current.isAfter(last)) {
    dates.push(current.format('YYYY-MM-DD'));
    current = current.add(1, 'day');
  }
  return dates;
};

const KennelGrid: React.FC = () => {
  const todayStr = dayjs().format('YYYY-MM-DD');
  const navigate = useNavigate();
  const location = useLocation();
  const { canManageSettings } = usePermissions();

  const navState = location.state as {
    highlightedBookingId?: number;
    startDate?: string;
    tabLabel?: string;
  } | null;

  const [tabs, setTabs] = useState<GridTab[]>([]);
  const [bookings, setBookings] = useState<BookingGridItem[]>([]);
  const [activeTab, setActiveTab] = useState(1);
  const [startDateStr, setStartDateStr] = useState<string>(todayStr);
  const [rangeEndStr, setRangeEndStr] = useState<string | null>(null);
  const [rangePickerOpen, setRangePickerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [popover, setPopover] = useState<PopoverState | null>(null);

  const gridRef = useRef<HTMLDivElement>(null);
  const rangeIconRef = useRef<HTMLButtonElement>(null);

  const isRangeMode = rangeEndStr !== null;

  const dates: string[] = isRangeMode
    ? buildDatesBetween(startDateStr, rangeEndStr!)
    : Array.from({ length: GRID_DAYS }, (_, i) =>
        dayjs(startDateStr).add(i, 'day').format('YYYY-MM-DD')
      );

  const endDateStr = dates[dates.length - 1];
  const kennels = tabs[activeTab]?.kennels ?? [];
  const totalWidth = DATE_COL_WIDTH + kennels.length * KENNEL_COL_WIDTH;

  const fetchKennels = useCallback(async () => {
    try {
      const data = await kennelsAPI.getGrouped();
      setTabs(deriveTabs(data));
    } catch {
      // grid shows empty on failure
    }
  }, []);

  const fetchBookings = useCallback(async () => {
    if (!startDateStr) return;
    try {
      const data = await bookingsAPI.getGrid(startDateStr, endDateStr);
      setBookings(data);
    } catch {
      setBookings([]);
    }
  }, [startDateStr, endDateStr]);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchKennels(), fetchBookings()]).finally(() =>
      setLoading(false)
    );
  }, []);

  useEffect(() => {
    fetchBookings();
  }, [startDateStr, rangeEndStr]);

  useEffect(() => {
    if (!navState || tabs.length === 0) return;
    if (navState.startDate) setStartDateStr(navState.startDate);
    if (navState.tabLabel) {
      const idx = tabs.findIndex((t) => t.label === navState.tabLabel);
      if (idx !== -1) setActiveTab(idx);
    }
  }, [tabs]);

  const handleSingleDateChange = (v: string) => {
    if (v) {
      setStartDateStr(v);
      setRangeEndStr(null);
    }
  };

  const handleRangeCheckInChange = (date: string) => {
    if (date) setStartDateStr(date);
  };
  const handleRangeCheckOutChange = (date: string) => {
    setRangeEndStr(date || null);
  };

  const handleClearRange = () => {
    setRangeEndStr(null);
    setRangePickerOpen(false);
  };

  const handleRangeIconClick = () => {
    if (isRangeMode) {
      handleClearRange();
    } else {
      setRangePickerOpen((prev) => !prev);
    }
  };

  const rangeLabel = isRangeMode
    ? `${dayjs(startDateStr).format('DD MMM')} – ${dayjs(rangeEndStr!).format('DD MMM YYYY')}`
    : null;

  const buildCellMap = (): Map<number, Map<string, CellInfo>> => {
    const map = new Map<number, Map<string, CellInfo>>();

    for (const booking of bookings) {
      const checkIn = booking.checkInDate.substring(0, 10);
      const checkOut = booking.checkOutDate.substring(0, 10);

      for (const bk of booking.kennels) {
        if (!map.has(bk.kennelId)) map.set(bk.kennelId, new Map());
        const kennelMap = map.get(bk.kennelId)!;

        const days = dates.filter((d) => d >= checkIn && d < checkOut);

        days.forEach((d, idx) => {
          let role: CellRole;
          if (days.length === 1) role = 'solo';
          else if (idx === 0) role = 'top';
          else if (idx === days.length - 1) role = 'bot';
          else role = 'mid';

          kennelMap.set(d, { role, booking, surnameCharIndex: idx - 1 });
        });
      }
    }
    return map;
  };

  const lookup = buildCellMap();

  const handleCellClick = (
    e: React.MouseEvent<HTMLElement>,
    info: CellInfo
  ) => {
    if (info.role === 'top' || info.role === 'solo') {
      setPopover({ anchorEl: e.currentTarget, booking: info.booking });
    }
  };

  const handlePopoverClose = () => setPopover(null);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100%"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <GridWrapper>
      <ControlsBar>
        <DatePickerWrapper>
          <DateRangePicker
            fontSize="1.5rem"
            mode="single"
            selectedDate={startDateStr}
            onDateChange={handleSingleDateChange}
          />
        </DatePickerWrapper>

        <StyledIconButton
          variant="bold"
          sx={{ '& svg': { color: colors.grey[50], fontSize: '4rem' } }}
          onClick={() =>
            setStartDateStr(
              dayjs(startDateStr).subtract(1, 'day').format('YYYY-MM-DD')
            )
          }
          aria-label="Previous day"
        >
          <ChevronLeft />
        </StyledIconButton>

        <StyledIconButton
          variant="bold"
          sx={{ '& svg': { color: colors.grey[50], fontSize: '4rem' } }}
          onClick={() =>
            setStartDateStr(
              dayjs(startDateStr).add(1, 'day').format('YYYY-MM-DD')
            )
          }
          aria-label="Next day"
        >
          <ChevronRight />
        </StyledIconButton>

        <TodayButton
          minWidth="115px"
          padding="0.08rem 1.25rem"
          onClick={() => {
            setStartDateStr(todayStr);
            setRangeEndStr(null);
            setRangePickerOpen(false);
          }}
        >
          TODAY
        </TodayButton>

        <Tooltip title={isRangeMode ? 'Clear range' : 'Select date range'}>
          <StyledIconButton
            ref={rangeIconRef}
            variant="bold"
            sx={{ '& svg': { fontSize: '2.9rem', color: colors.grey[50] } }}
            onClick={handleRangeIconClick}
            aria-label="Select date range"
          >
            <DateRange />
          </StyledIconButton>
        </Tooltip>

        {rangeLabel && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              px: 1.5,
              py: 0.5,
              borderRadius: '1rem',
              backgroundColor: colors.primary.light,
              flexShrink: 0,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: colors.primary.dark,
                fontWeight: 700,
                fontSize: '1.6rem',
                whiteSpace: 'nowrap',
              }}
            >
              {rangeLabel}
            </Typography>
            <Close
              sx={{
                fontSize: '1.6rem',
                color: colors.primary.dark,
                cursor: 'pointer',
              }}
              onClick={handleClearRange}
            />
          </Box>
        )}

        <DateRangePicker
          mode="range"
          showControls={false}
          open={rangePickerOpen}
          onClose={() => setRangePickerOpen(false)}
          anchorRef={rangeIconRef}
          checkInDate={startDateStr}
          checkOutDate={rangeEndStr ?? ''}
          onCheckInChange={handleRangeCheckInChange}
          onCheckOutChange={handleRangeCheckOutChange}
        />
      </ControlsBar>

      <StyledTabs
        value={activeTab}
        onChange={(_: React.SyntheticEvent, v: number) => setActiveTab(v)}
        variant="scrollable"
        scrollButtons="auto"
      >
        {tabs.map((t) => (
          <StyledTab key={t.label} label={t.label} />
        ))}
      </StyledTabs>

      <GridScroller ref={gridRef}>
        <StickyHeaderRow totalWidth={totalWidth}>
          <StickyDateCorner>
            <Typography variant="caption" fontWeight="bold">
              Date
            </Typography>
          </StickyDateCorner>
          {kennels.map((k) => (
            <KennelHeaderCell key={k.id}>
              <Typography variant="caption" fontWeight="bold">
                {String(k.kennelNumber).padStart(3, '0')}
              </Typography>
            </KennelHeaderCell>
          ))}
        </StickyHeaderRow>

        {dates.map((dateStr) => {
          const isToday = dateStr === todayStr;
          return (
            <DateRow key={dateStr} totalWidth={totalWidth}>
              <StickyDateCell isToday={isToday}>
                <DateLabel variant="caption" isToday={isToday}>
                  {dateStr}
                </DateLabel>
              </StickyDateCell>

              {kennels.map((k) => {
                const info = lookup.get(k.id)?.get(dateStr);

                if (!info) return <EmptyKennelCell key={k.id} />;

                const isHighlighted =
                  navState?.highlightedBookingId === info.booking.id;
                const color = getBookingColor(
                  info.booking.status,
                  isHighlighted
                );
                const isTop = info.role === 'top' || info.role === 'solo';
                const isBot = info.role === 'bot' || info.role === 'solo';
                const char =
                  info.role === 'mid'
                    ? (info.booking.clientSurname[info.surnameCharIndex] ?? '')
                    : '';

                return (
                  <BookingCell
                    key={k.id}
                    bookingColor={color}
                    isTop={isTop}
                    isBot={isBot}
                    onClick={(e: React.MouseEvent<HTMLElement>) =>
                      handleCellClick(e, info)
                    }
                  >
                    {isTop && (
                      <CellLabel variant="caption">{info.booking.id}</CellLabel>
                    )}
                    {info.role === 'mid' && (
                      <CellMidChar variant="caption">{char}</CellMidChar>
                    )}
                  </BookingCell>
                );
              })}
            </DateRow>
          );
        })}
      </GridScroller>

      <Popover
        open={Boolean(popover)}
        anchorEl={popover?.anchorEl ?? null}
        onClose={handlePopoverClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        disableRestoreFocus
      >
        {popover && (
          <Paper sx={{ p: 1.5, minWidth: 200 }}>
            <Typography variant="body2" fontWeight="bold">
              Booking #{popover.booking.id}
            </Typography>
            <Typography variant="body2">
              {popover.booking.clientName} {popover.booking.clientSurname}
            </Typography>
            <Typography
              variant="caption"
              display="block"
              color="text.secondary"
            >
              {dayjs(popover.booking.checkInDate).format('DD MMM YYYY')} —{' '}
              {dayjs(popover.booking.checkOutDate).format('DD MMM YYYY')}
            </Typography>
            <Typography
              variant="caption"
              display="block"
              color="text.secondary"
            >
              Status: {popover.booking.status}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
              {canManageSettings && (
                <>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled
                    sx={{
                      px: { xs: 0.75, sm: 2 },
                      minWidth: { xs: '60px' },
                      flexGrow: 1,
                    }}
                  >
                    Move
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled
                    sx={{
                      px: { xs: 0.75, sm: 2 },
                      minWidth: { xs: '60px' },
                      flexGrow: 1,
                    }}
                  >
                    Split
                  </Button>
                </>
              )}
              <Button
                size="small"
                variant="contained"
                sx={{
                  px: { xs: 0.75, sm: 2 },
                  minWidth: { xs: '60px' },
                  flexGrow: 1,
                }}
                onClick={() => {
                  handlePopoverClose();
                  navigate(`/booking/${popover.booking.id}`);
                }}
              >
                Open
              </Button>
            </Box>
          </Paper>
        )}
      </Popover>
    </GridWrapper>
  );
};

export default KennelGrid;
